import NGHeading from '@NGComponents/NGHeading';
import NGIcon from '@NGComponents/NGIcon';
import { EFontFamily, ETextTransform, ETextType } from '@NGComponents/NGText/NGText.enum';
import { EMediaQuery, ESize, ETextColor, EThemeColor } from '@NGUtils/enums';
import ItemButton from '@components/common/ItemButton';
import HotkeyTooltip from '@components/tooltips/HotkeyTooltip';
import useShowAllQuests from '@devHub/store/hooks/useShowAllQuests';
import { pushURL } from '@functions/functions';
import useResponsive from '@hooks/contexts/useResponsive';
import useCurrentPageId from '@store/appState/hooks/useCurrentPageId';
import useCurrentSubpage from '@store/appState/hooks/useCurrentSubpage';
import { APP_FIRST_SUBPAGES, APP_SUB_MENUS, NAVBAR_LINKS, URL_PARAMS } from '@utils/variables';
import { useRouter } from 'next/router';
import { FC, ReactNode, useCallback, useEffect, useMemo } from 'react';
import { IAppLink } from '../../../types/global';
import { StyledNavLinksListItem } from '../Mainlinks/Mainlinks.styles';
import { StyledNavLinksList } from '../Navbar/Navbar.styles';
import NavbarAvatar from '../NavbarAvatar';
import { StyledSubLinkItem, StyledSubmenusUnderline } from './Sublinks.styles';
import { ISublinks, ISubmenu } from './Sublinks.type';

const Sublinks: FC<ISublinks> = ({ activeId, onClick, mobilePageId, isHidden, ...otherProps }) => {
	const [currentPageId] = useCurrentPageId();
	const [{ id: currentSubpageId }] = useCurrentSubpage();
	const [showAllQuests] = useShowAllQuests();

	const router = useRouter();

	const { isSmallerThanMd } = useResponsive();

	const forceQuestActivated = useMemo(
		() => !!router?.pathname && router.pathname.includes('hub/quests/'),
		[router.pathname]
	);

	const getIconColor = useCallback(
		(
			id: string
		):
			| {
					text: ETextColor;
					theme: EThemeColor;
			  }
			| undefined => {
			if (
				(forceQuestActivated && id === APP_SUB_MENUS.devhub.quests.id) ||
				(!forceQuestActivated && (currentSubpageId === id || (!currentSubpageId && APP_FIRST_SUBPAGES.includes(id))))
			) {
				return {
					text: ETextColor.white,
					theme: EThemeColor.white
				};
			}

			return undefined;
		},
		[forceQuestActivated, currentSubpageId]
	);

	const handleClick = useCallback(
		(id: string) => {
			if (onClick) {
				onClick();
			}

			// get currentPage
			const pageWithSubmenus = NAVBAR_LINKS.find(({ id: linkId }) => linkId === (mobilePageId || currentPageId));

			if (!pageWithSubmenus) {
				return;
			}

			pushURL(pageWithSubmenus.href, id);
		},
		[onClick, mobilePageId, currentPageId]
	);

	const submenus: ISubmenu[] = useMemo(() => {
		try {
			if (!activeId) {
				return [];
			}

			return NAVBAR_LINKS.find(({ id: linkId }: IAppLink) => linkId === activeId)?.submenus || [];
		} catch (err) {
			console.error('set submenus error', err);
			return [];
		}
	}, [activeId]);

	const renderSubmenuLabel = useCallback(() => {
		if (currentSubpageId === APP_SUB_MENUS.devhub.quests.id) {
			return showAllQuests ? 'Quests' : 'Standalone Quests';
		}

		return submenus.find(({ id }) => id === currentSubpageId)?.label || submenus?.[0].label || 'Overview';
	}, [currentSubpageId, showAllQuests, submenus]);

	useEffect(() => {
		if ((currentSubpageId && currentPageId === activeId && router?.query?.s) || !submenus?.length || !router?.query) {
			return;
		}

		try {
			// @ts-ignore
			delete window[URL_PARAMS.subpage];
			// @ts-ignore
			delete window[URL_PARAMS.selectedQuest];
			// @ts-ignore
			delete window[URL_PARAMS.selectedCampaign];
		} catch (err) {
			console.error('delete window error', err);
		}
	}, [activeId, currentPageId, currentSubpageId, router?.query, submenus?.length]);

	return (
		<StyledNavLinksList data-cy='sublinks' isHidden={isHidden} {...otherProps}>
			{submenus?.length ? (
				<>
					{!isSmallerThanMd ? (
						<StyledSubLinkItem>
							<NGHeading as='p' type={ETextType.h4} textColor={ETextColor.gold} fontFamily={EFontFamily.cinzel}>
								{renderSubmenuLabel()}
							</NGHeading>
						</StyledSubLinkItem>
					) : null}

					{submenus.map((submenu: ISubmenu, index: number): ReactNode => {
						const { type, icon, label, isDisabled, id } = submenu;
						const color = getIconColor(id);

						return (
							<StyledSubLinkItem key={type} isDisabled={isDisabled}>
								{isSmallerThanMd ? (
									<NGHeading
										as='p'
										type={ETextType.h4}
										textColor={isDisabled ? ETextColor.transparentGold : color?.text || ETextColor.grey}
										shadowColor={color?.theme}
										textTransform={ETextTransform.uppercase}
										firstLetterFactor={1.2}
										fontFamily={EFontFamily.cinzel}
									>
										{label}
									</NGHeading>
								) : (
									<NGIcon
										type={icon}
										size={ESize.s}
										color={isDisabled ? ETextColor.transparentGold : color?.text || ETextColor.transparentGold}
										shadowColor={color?.theme}
									/>
								)}

								<HotkeyTooltip
									text={label}
									hotkeys={[`${index + 1}`]}
									onHotkeyPress={() => handleClick(id)}
									disabled={isSmallerThanMd || isDisabled}
									inactive={isSmallerThanMd || isDisabled}
								>
									<ItemButton
										data-cy={`navbar-submenu-${type}`}
										onClick={() => handleClick(id)}
										disabled={isDisabled}
									/>
								</HotkeyTooltip>
							</StyledSubLinkItem>
						);
					})}
				</>
			) : null}

			{!isSmallerThanMd ? (
				<StyledNavLinksListItem hiddenRange={[EMediaQuery.md, undefined]}>
					<NavbarAvatar />
				</StyledNavLinksListItem>
			) : null}

			{submenus?.length ? <StyledSubmenusUnderline /> : null}
		</StyledNavLinksList>
	);
};

export { Sublinks };
